/**
 * Import graph (advanced code understanding). Parses each file's imports via the
 * TypeScript compiler API, resolves relative specifiers against the indexed repo
 * (extension + `/index` probing), and classifies each as `internal` (resolved to a
 * repo file), `external` (a bare package), or `unresolved` (a relative import that
 * points at no existing file — i.e. a BROKEN import). Persisted with the index so
 * retrieval/agents can pull a file's dependencies and surface broken links.
 */
import * as ts from 'typescript';
import type { ScannedFile } from './scan.js';

const TS_LIKE = ['.ts', '.tsx', '.mts', '.cts', '.js', '.jsx', '.mjs', '.cjs'];
const RESOLVE_EXTS = ['', '.ts', '.tsx', '.d.ts', '.mts', '.cts', '.js', '.jsx', '.mjs', '.cjs', '.json'];
const INDEX_FILES = ['index.ts', 'index.tsx', 'index.mts', 'index.cts', 'index.js', 'index.jsx'];
// TS/ESM lets a relative import carry a `.js`-family extension that resolves to the
// `.ts` source on disk (`import './x.js'` → `x.ts`). Map those so they aren't false broken.
const JS_TO_TS: Record<string, string[]> = {
  '.js': ['.ts', '.tsx', '.d.ts'],
  '.jsx': ['.tsx'],
  '.mjs': ['.mts'],
  '.cjs': ['.cts'],
};

export type ImportKind = 'internal' | 'external' | 'unresolved';

export interface ImportRef {
  readonly specifier: string;
  /** Repo-relative POSIX path when resolved to an indexed file, else null. */
  readonly resolved: string | null;
  readonly kind: ImportKind;
}

export interface FileImports {
  readonly file: string;
  readonly imports: readonly ImportRef[];
}

function isTsLike(path: string): boolean {
  return TS_LIKE.some((e) => path.endsWith(e));
}

function scriptKind(path: string): ts.ScriptKind {
  if (path.endsWith('.tsx')) return ts.ScriptKind.TSX;
  if (path.endsWith('.jsx')) return ts.ScriptKind.JSX;
  if (/\.(?:js|mjs|cjs)$/.test(path)) return ts.ScriptKind.JS;
  return ts.ScriptKind.TS;
}

/** Module specifiers imported by a file (static import/export-from + dynamic import + require). */
export function extractImportSpecifiers(path: string, content: string): string[] {
  const sf = ts.createSourceFile(path, content, ts.ScriptTarget.Latest, false, scriptKind(path));
  const out: string[] = [];
  const visit = (node: ts.Node): void => {
    if (ts.isImportDeclaration(node) && ts.isStringLiteral(node.moduleSpecifier)) {
      out.push(node.moduleSpecifier.text);
    } else if (
      ts.isExportDeclaration(node) &&
      node.moduleSpecifier &&
      ts.isStringLiteral(node.moduleSpecifier)
    ) {
      out.push(node.moduleSpecifier.text);
    } else if (ts.isCallExpression(node)) {
      const isRequire = ts.isIdentifier(node.expression) && node.expression.text === 'require';
      const isDynamic = node.expression.kind === ts.SyntaxKind.ImportKeyword;
      const arg = node.arguments[0];
      if ((isRequire || isDynamic) && arg && ts.isStringLiteral(arg)) out.push(arg.text);
    }
    ts.forEachChild(node, visit);
  };
  visit(sf);
  return out;
}

function posixDirname(p: string): string {
  const i = p.lastIndexOf('/');
  return i === -1 ? '' : p.slice(0, i);
}

/** Resolve `rel` against `baseDir` (both repo-relative POSIX), normalizing ./ and ../ */
function posixResolve(baseDir: string, rel: string): string {
  const stack: string[] = [];
  for (const part of `${baseDir}/${rel}`.split('/')) {
    if (part === '' || part === '.') continue;
    if (part === '..') stack.pop();
    else stack.push(part);
  }
  return stack.join('/');
}

/** Resolve a relative specifier to an indexed repo file, or null when broken. */
function resolveRelative(fromFile: string, spec: string, fileSet: ReadonlySet<string>): string | null {
  const target = posixResolve(posixDirname(fromFile), spec);
  // 1. Exact path (the specifier already names an existing file).
  if (fileSet.has(target)) return target;
  // 2. A `.js`-family extension that maps to TS source on disk.
  const jsExt = Object.keys(JS_TO_TS).find((e) => target.endsWith(e));
  if (jsExt) {
    const base = target.slice(0, -jsExt.length);
    for (const tsExt of JS_TO_TS[jsExt] ?? []) {
      if (fileSet.has(`${base}${tsExt}`)) return `${base}${tsExt}`;
    }
  }
  // 3. Extensionless specifier — probe known extensions.
  for (const ext of RESOLVE_EXTS) {
    if (ext && fileSet.has(`${target}${ext}`)) return `${target}${ext}`;
  }
  // 4. Directory import — probe `<dir>/index.*`.
  for (const idx of INDEX_FILES) {
    const cand = target ? `${target}/${idx}` : idx;
    if (fileSet.has(cand)) return cand;
  }
  return null;
}

/** Build the per-file import list for the whole repo. */
export function buildImportGraph(files: readonly ScannedFile[]): FileImports[] {
  const fileSet = new Set(files.map((f) => f.path));
  const out: FileImports[] = [];
  for (const f of files) {
    if (!isTsLike(f.path)) continue;
    let specs: string[];
    try {
      specs = extractImportSpecifiers(f.path, f.content);
    } catch {
      continue; // never let a parse error abort indexing
    }
    if (specs.length === 0) continue;
    const imports: ImportRef[] = specs.map((specifier) => {
      if (specifier.startsWith('.')) {
        const resolved = resolveRelative(f.path, specifier, fileSet);
        return { specifier, resolved, kind: resolved ? 'internal' : 'unresolved' };
      }
      return { specifier, resolved: null, kind: 'external' };
    });
    out.push({ file: f.path, imports });
  }
  return out;
}

export interface ImportStats {
  readonly files: number;
  readonly edges: number;
  readonly internal: number;
  readonly external: number;
  readonly broken: number;
}

export interface BrokenImport {
  readonly file: string;
  readonly specifier: string;
}

/** Queryable import graph: forward + reverse edges + broken-link detection. */
export class ImportGraph {
  private readonly byFile = new Map<string, ImportRef[]>();
  private readonly importers = new Map<string, Set<string>>();

  static fromJSON(data: readonly FileImports[]): ImportGraph {
    const g = new ImportGraph();
    for (const fi of data) {
      g.byFile.set(fi.file, [...fi.imports]);
      for (const imp of fi.imports) {
        if (imp.resolved) {
          const set = g.importers.get(imp.resolved) ?? new Set<string>();
          set.add(fi.file);
          g.importers.set(imp.resolved, set);
        }
      }
    }
    return g;
  }

  /** What `file` imports. */
  importsOf(file: string): ImportRef[] {
    return this.byFile.get(file)?.slice() ?? [];
  }

  /** Repo files that import `file` (reverse deps). */
  importersOf(file: string): string[] {
    return [...(this.importers.get(file) ?? [])];
  }

  /** Resolved internal files `file` depends on (1 hop). */
  dependenciesOf(file: string): string[] {
    return this.importsOf(file)
      .map((i) => i.resolved)
      .filter((r): r is string => r !== null);
  }

  /** Files that have at least one recorded import. */
  files(): string[] {
    return [...this.byFile.keys()];
  }

  /** Unresolved relative imports across the repo (broken links). */
  broken(): BrokenImport[] {
    const out: BrokenImport[] = [];
    for (const [file, imps] of this.byFile) {
      for (const imp of imps) {
        if (imp.kind === 'unresolved') out.push({ file, specifier: imp.specifier });
      }
    }
    return out;
  }

  stats(): ImportStats {
    let edges = 0;
    let internal = 0;
    let external = 0;
    let broken = 0;
    for (const imps of this.byFile.values()) {
      for (const imp of imps) {
        edges++;
        if (imp.kind === 'internal') internal++;
        else if (imp.kind === 'external') external++;
        else broken++;
      }
    }
    return { files: this.byFile.size, edges, internal, external, broken };
  }
}
